import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  getTechCurrentAssignment,
  getTechDashboard,
  type TechCurrentAssignment,
  type TechDashboardResponse,
} from '@/lib/tech-api-client';

function formatCents(cents: number): string {
  const dollars = Number(cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export default function TechDashboardPage() {
  const navigate = useNavigate();
  const {
    accessToken,
    technician,
    isOnline,
    pendingEnrollmentCount,
    pendingEnrollmentSyncing,
    pendingEnrollmentError,
    syncPendingEnrollments,
  } = useTechAuth();
  const [data, setData] = useState<TechDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAssignment, setCurrentAssignment] = useState<TechCurrentAssignment | null>(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignmentWarnings, setAssignmentWarnings] = useState<string[]>([]);

  const load = async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);
    setAssignmentError(null);
    try {
      const res = await getTechDashboard(accessToken);
      setData(res);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }

    setAssignmentLoading(true);
    setAssignmentWarnings([]);
    try {
      const res = await getTechCurrentAssignment(accessToken);
      setCurrentAssignment(res.current_assignment ?? null);
      setAssignmentWarnings(Array.isArray(res.warnings) ? res.warnings : []);
    } catch (e) {
      setCurrentAssignment(null);
      setAssignmentWarnings([]);
      setAssignmentError(getErrorMessage(e));
    } finally {
      setAssignmentLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const spiffs = data?.stats?.ytd?.spiffs;
  const recentSales = useMemo(() => data?.recent_sales || [], [data?.recent_sales]);

  const assignmentSummary = useMemo(() => {
    if (!currentAssignment) return null;

    const { location } = currentAssignment;
    const cityState = [location?.city, location?.state].filter(Boolean).join(', ');
    const line2 = [cityState, location?.zipCode].filter(Boolean).join(' ');

    const kind = currentAssignment.kind === 'next' ? 'next' : 'current';
    const label = kind === 'next' ? 'Next assignment' : 'Current assignment';

    const customerName = [currentAssignment.customer?.first_name, currentAssignment.customer?.last_name]
      .filter(Boolean)
      .join(' ');

    const scheduledForRaw = currentAssignment.timestamps?.scheduled_for || null;
    let scheduledTime: string | null = null;
    if (scheduledForRaw) {
      const d = new Date(scheduledForRaw);
      if (!Number.isNaN(d.getTime())) {
        scheduledTime = d.toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
        });
      }
    }

    return {
      kind,
      label,
      jobId: currentAssignment.job?.id || null,
      jobNumber: currentAssignment.job?.number || null,
      customerName: customerName || null,
      scheduledTime,
      line1: location?.address1 || null,
      line2: line2 || null,
    };
  }, [currentAssignment]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {technician?.full_name ? `Welcome, ${technician.full_name}.` : 'Welcome.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {!isOnline && (
            <div className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">Offline</div>
          )}

          {pendingEnrollmentCount > 0 && (
            <button
              type="button"
              onClick={() => syncPendingEnrollments()}
              disabled={!isOnline || pendingEnrollmentSyncing}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              title={
                isOnline
                  ? pendingEnrollmentSyncing
                    ? 'Syncing pending enrollments…'
                    : 'Sync pending enrollments'
                  : 'Go online to sync pending enrollments'
              }
            >
              Pending Sync: {pendingEnrollmentCount}
              {pendingEnrollmentSyncing ? '…' : ''}
            </button>
          )}

          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {pendingEnrollmentError && (
        <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-900 font-medium">
              Pending enrollment sync failed: {pendingEnrollmentError}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-sm text-gray-500">{assignmentSummary?.label ?? 'Current assignment'}</div>

            {assignmentLoading ? (
              <div className="mt-2 text-gray-700">Detecting current job…</div>
            ) : assignmentSummary ? (
              <div className="mt-2">
                <div className="text-xl font-bold text-gray-900">
                  {assignmentSummary.jobNumber
                    ? assignmentSummary.kind === 'next'
                      ? `Next job #${assignmentSummary.jobNumber}`
                      : `Job #${assignmentSummary.jobNumber}`
                    : assignmentSummary.kind === 'next'
                      ? 'Next job'
                      : 'Current job'}
                </div>
                {assignmentSummary.customerName ? (
                  <div className="mt-1 text-sm font-semibold text-gray-800">{assignmentSummary.customerName}</div>
                ) : null}

                {assignmentSummary.scheduledTime ? (
                  <div className="mt-1 text-sm text-gray-600">Scheduled: {assignmentSummary.scheduledTime}</div>
                ) : null}

                <div className="mt-1 text-sm text-gray-700">{assignmentSummary.line1 || '—'}</div>
                {assignmentSummary.line2 ? <div className="text-sm text-gray-500">{assignmentSummary.line2}</div> : null}
              </div>
            ) : (
              <div className="mt-2 text-gray-700">No current or upcoming job detected for today.</div>
            )}

            {assignmentError ? (
              <div className="mt-2 text-sm text-amber-800">Current assignment: {assignmentError}</div>
            ) : assignmentWarnings.length ? (
              <div className="mt-2 text-sm text-amber-800">Current assignment: {assignmentWarnings.join(' ')}</div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {assignmentSummary?.jobId ? (
              <button
                type="button"
                onClick={() => navigate(`/job/${encodeURIComponent(assignmentSummary.jobId)}`)}
                className="inline-flex items-center px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-900 font-semibold hover:bg-gray-50"
              >
                {assignmentSummary.kind === 'next' ? 'Open next job' : 'Open current job'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => navigate('/presentation')}
              className="inline-flex items-center px-4 py-2 rounded-xl bg-brand-primary text-white font-semibold hover:brightness-110"
            >
              Start Presentation
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Memberships sold today</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {data?.stats?.today?.memberships_sold ?? '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Memberships sold YTD</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {data?.stats?.ytd?.memberships_sold ?? '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Spiffs pending (YTD)</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {spiffs ? formatCents(spiffs.pending_cents) : '—'}
          </div>
          {spiffs && (
            <div className="mt-2 text-xs text-gray-500">
              Paid: {formatCents(spiffs.paid_cents)} • Cancelled: {formatCents(spiffs.cancelled_cents)}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Recent sales</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Sold at</th>
                <th className="py-2 pr-4">Member</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentSales.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-6 text-gray-500">
                    No sales yet.
                  </td>
                </tr>
              ) : (
                recentSales.map((row) => {
                  const m = row.membership;
                  const name = [m?.customer_first_name, m?.customer_last_name].filter(Boolean).join(' ');
                  const soldAt = row.sold_at ? new Date(row.sold_at).toLocaleString() : '—';

                  return (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="py-3 pr-4 whitespace-nowrap">{soldAt}</td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{name || '—'}</div>
                        <div className="text-xs text-gray-500">{m?.membership_number || m?.id || ''}</div>
                      </td>
                      <td className="py-3 pr-4">{m?.plan_name || '—'}</td>
                      <td className="py-3">{m?.status || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
